import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getAdminEditionContent, getAdminSpeakers,
  createSpeaker, assignSpeakerToEdition, removeSpeakerFromEdition,
  sendSpeakerLink, createMaterial, deleteMaterial, adminDownloadMaterial,
  type Speaker, type CourseMaterial,
} from '../../api/content';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fileIcon(type: string) {
  switch (type) {
    case 'pdf': return '📄';
    case 'ppt': case 'pptx': return '📊';
    case 'video': return '🎬';
    case 'recording': return '🎥';
    case 'link': return '🔗';
    default: return '📎';
  }
}

function formatBytes(bytes?: number | null) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Speaker Card ──────────────────────────────────────────────────────────
function SpeakerCard({ speaker, editionId, onRemove }: {
  speaker: Speaker & { access_token?: string }; editionId: string; onRemove: () => void;
}) {
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!confirm(`Rimuovere ${speaker.nome} ${speaker.cognome} da questa edizione?`)) return;
    setRemoving(true);
    try { await removeSpeakerFromEdition(editionId, speaker.id); onRemove(); }
    finally { setRemoving(false); }
  };

  const speakerLink = speaker.access_token
    ? `${window.location.origin}/speaker/${speaker.access_token}`
    : null;

  const copyLink = async () => {
    if (!speakerLink) return;
    await navigator.clipboard.writeText(speakerLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendLink = async () => {
    setSending(true);
    setSendMsg(null);
    try {
      await sendSpeakerLink(editionId, speaker.id);
      setSendMsg('✓ Email inviata');
    } catch (e: any) {
      setSendMsg(e?.response?.data?.error ?? 'Errore invio');
    } finally {
      setSending(false);
      setTimeout(() => setSendMsg(null), 4000);
    }
  };

  return (
    <div className="card" style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
          overflow: 'hidden', background: 'var(--gray-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {speaker.photo_url
            ? <img src={speaker.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: '1.5rem', color: 'var(--gray-300)' }}>👤</span>
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{speaker.nome} {speaker.cognome}</div>
          {speaker.azienda && <div style={{ fontSize: '0.875rem', color: 'var(--primary-light)' }}>{speaker.azienda}</div>}
          <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{speaker.role}</div>
          {speaker.email && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>{speaker.email}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button
            className="btn btn-primary"
            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem', opacity: (!speakerLink || !speaker.email) ? 0.45 : 1 }}
            onClick={handleSendLink}
            disabled={sending || !speakerLink || !speaker.email}
            title={
              !speakerLink ? 'Token non ancora generato — riavvia il backend' :
              !speaker.email ? 'Email non configurata — modifica il relatore' :
              `Invia link a ${speaker.email}`
            }
          >
            {sending ? '…' : '✉ Invia link'}
          </button>
          <button className="btn btn-danger" style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem' }}
            onClick={handleRemove} disabled={removing}>
            {removing ? '…' : 'Rimuovi'}
          </button>
        </div>
      </div>
      {sendMsg && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: sendMsg.startsWith('✓') ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>
          {sendMsg}
        </div>
      )}
      {speakerLink && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            readOnly value={speakerLink}
            className="form-control"
            style={{ fontSize: '0.75rem', color: 'var(--gray-500)', background: 'var(--gray-50)' }}
            onFocus={e => e.target.select()}
          />
          <button
            className="btn btn-secondary"
            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem', whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={copyLink}
          >
            {copied ? '✓ Copiato' : 'Copia link'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Add Speaker Modal ────────────────────────────────────────────────────────
function AddSpeakerModal({ editionId, onClose, onAdded }: {
  editionId: string; onClose: () => void; onAdded: () => void;
}) {
  const [allSpeakers, setAllSpeakers] = useState<Speaker[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [role, setRole] = useState('Relatore');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'existing' | 'new'>('existing');

  // New speaker form
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [bio, setBio] = useState('');
  const [azienda, setAzienda] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const photoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAdminSpeakers().then(setAllSpeakers);
  }, []);

  const handleAssign = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await assignSpeakerToEdition(editionId, selectedId, role);
      onAdded();
      onClose();
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!nome || !cognome) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('nome', nome); fd.append('cognome', cognome);
      fd.append('bio', bio); fd.append('azienda', azienda);
      fd.append('linkedin_url', linkedin);
      if (photoRef.current?.files?.[0]) fd.append('photo', photoRef.current.files[0]);
      const speaker = await createSpeaker(fd);
      await assignSpeakerToEdition(editionId, speaker.id, role);
      onAdded();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
    }} onClick={onClose}>
      <div className="card" style={{ width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0 }}>Aggiungi Relatore</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {(['existing', 'new'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`btn ${tab === t ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1, fontSize: '0.875rem' }}>
              {t === 'existing' ? 'Relatore esistente' : 'Nuovo relatore'}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label className="form-label">Ruolo nell'edizione</label>
          <input className="form-control" value={role} onChange={e => setRole(e.target.value)} placeholder="Es. Relatore, Moderatore, Ospite" />
        </div>

        {tab === 'existing' ? (
          <>
            <div className="form-group">
              <label className="form-label">Seleziona relatore</label>
              <select className="form-control" value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                <option value="">— Scegli —</option>
                {allSpeakers.map(s => (
                  <option key={s.id} value={s.id}>{s.nome} {s.cognome} {s.azienda ? `(${s.azienda})` : ''}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }}
              onClick={handleAssign} disabled={!selectedId || saving}>
              {saving ? 'Salvataggio…' : 'Aggiungi'}
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Cognome *</label>
                <input className="form-control" value={cognome} onChange={e => setCognome(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Azienda</label>
              <input className="form-control" value={azienda} onChange={e => setAzienda(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea className="form-control" rows={3} value={bio} onChange={e => setBio(e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <div className="form-group">
              <label className="form-label">LinkedIn URL</label>
              <input className="form-control" type="url" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="form-group">
              <label className="form-label">Foto (JPG/PNG, max 5MB)</label>
              <input type="file" ref={photoRef} accept="image/jpeg,image/png,image/webp" className="form-control" />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }}
              onClick={handleCreate} disabled={!nome || !cognome || saving}>
              {saving ? 'Salvataggio…' : 'Crea e aggiungi'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Add Material Modal ───────────────────────────────────────────────────────
function AddMaterialModal({ editionId, onClose, onAdded }: {
  editionId: string; onClose: () => void; onAdded: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('pdf');
  const [availableFrom, setAvailableFrom] = useState<'registration' | 'attended'>('registration');
  const [externalUrl, setExternalUrl] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isFileType = ['pdf', 'ppt', 'pptx', 'video'].includes(type);

  const handleSave = async () => {
    if (!title) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('edition_id', editionId);
      fd.append('title', title);
      fd.append('description', description);
      fd.append('type', type);
      fd.append('available_from', availableFrom);
      fd.append('sort_order', String(sortOrder));
      if (externalUrl) fd.append('external_url', externalUrl);
      if (isFileType && fileRef.current?.files?.[0]) fd.append('file', fileRef.current.files[0]);
      await createMaterial(fd);
      onAdded();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem',
    }} onClick={onClose}>
      <div className="card" style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0 }}>Aggiungi Materiale</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Slide del corso" />
        </div>

        <div className="form-group">
          <label className="form-label">Tipo</label>
          <select className="form-control" value={type} onChange={e => setType(e.target.value)}>
            <option value="pdf">PDF</option>
            <option value="ppt">PowerPoint (PPT)</option>
            <option value="pptx">PowerPoint (PPTX)</option>
            <option value="recording">Registrazione Zoom/Video</option>
            <option value="video">Video</option>
            <option value="link">Link esterno</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Descrizione</label>
          <textarea className="form-control" rows={2} value={description}
            onChange={e => setDescription(e.target.value)} style={{ resize: 'vertical' }} />
        </div>

        <div className="form-group">
          <label className="form-label">Disponibile da</label>
          <select className="form-control" value={availableFrom} onChange={e => setAvailableFrom(e.target.value as any)}>
            <option value="registration">Dopo la registrazione (subito)</option>
            <option value="attended">Solo dopo la partecipazione confermata</option>
          </select>
        </div>

        {isFileType && (
          <div className="form-group">
            <label className="form-label">File (max 100MB)</label>
            <input type="file" ref={fileRef} className="form-control"
              accept=".pdf,.ppt,.pptx,.mp4,.webm" />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">
            {type === 'link' ? 'URL *' : type === 'recording' ? 'Link registrazione (Zoom, YouTube, Drive…)' : 'URL esterno alternativo al file'}
          </label>
          <input className="form-control" type="url" value={externalUrl}
            onChange={e => setExternalUrl(e.target.value)}
            placeholder="https://..." />
        </div>

        <div className="form-group">
          <label className="form-label">Ordine di visualizzazione</label>
          <input className="form-control" type="number" value={sortOrder}
            onChange={e => setSortOrder(Number(e.target.value))} />
        </div>

        <button className="btn btn-primary" style={{ width: '100%' }}
          onClick={handleSave} disabled={!title || saving}>
          {saving ? 'Salvataggio…' : 'Aggiungi materiale'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminEditionContentPage() {
  const { id: editionId } = useParams<{ id: string }>();
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [materials, setMaterials] = useState<CourseMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddSpeaker, setShowAddSpeaker] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  const load = async () => {
    if (!editionId) return;
    try {
      const data = await getAdminEditionContent(editionId);
      setSpeakers(data.speakers);
      setMaterials(data.materials);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [editionId]);

  const handleDeleteMaterial = async (id: string, title: string) => {
    if (!confirm(`Eliminare "${title}"?`)) return;
    await deleteMaterial(id);
    load();
  };

  if (loading) return (
    <div className="loading-container"><div className="spinner spinner-lg" /></div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <Link to="/admin/editions" style={{ color: 'var(--gray-400)', fontSize: '0.875rem', textDecoration: 'none' }}>
            ← Edizioni
          </Link>
          <h2 style={{ margin: '0.25rem 0 0' }}>Contenuti Edizione</h2>
        </div>
      </div>

      {/* ── Relatori ─────────────────────────────────────────────────────── */}
      <section style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>👥 Relatori ({speakers.length})</h3>
          <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}
            onClick={() => setShowAddSpeaker(true)}>
            + Aggiungi relatore
          </button>
        </div>

        {speakers.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '2rem' }}>
            Nessun relatore assegnato a questa edizione.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {speakers.map(s => (
              <SpeakerCard key={s.id} speaker={s} editionId={editionId!} onRemove={load} />
            ))}
          </div>
        )}
      </section>

      {/* ── Materiali ─────────────────────────────────────────────────────── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>📁 Materiali e Registrazioni ({materials.length})</h3>
          <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}
            onClick={() => setShowAddMaterial(true)}>
            + Aggiungi materiale
          </button>
        </div>

        {materials.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '2rem' }}>
            Nessun materiale caricato per questa edizione.
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Titolo</th>
                  <th>Dimensione</th>
                  <th>Disponibile</th>
                  <th>Ordine</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {materials.map(m => (
                  <tr key={m.id}>
                    <td>
                      <span title={m.type}>{fileIcon(m.type)}</span>{' '}
                      <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', textTransform: 'uppercase' }}>{m.type}</span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{m.title}</div>
                      {m.description && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>{m.description}</div>}
                    </td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{formatBytes(m.file_size_bytes)}</td>
                    <td>
                      <span style={{
                        fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: 20,
                        background: m.available_from === 'attended' ? '#fef3c7' : '#d1fae5',
                        color: m.available_from === 'attended' ? '#92400e' : '#065f46',
                        fontWeight: 500,
                      }}>
                        {m.available_from === 'attended' ? 'Solo partecipanti' : 'Dopo iscrizione'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--gray-400)', fontSize: '0.875rem' }}>{m.sort_order}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {m.file_path && (
                          <button className="btn btn-secondary" style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
                            onClick={() => adminDownloadMaterial(m.id, m.title, m.file_path!.substring(m.file_path!.lastIndexOf('.')))}>
                            ⬇ Scarica
                          </button>
                        )}
                        {m.external_url && (
                          <a href={m.external_url} target="_blank" rel="noopener noreferrer"
                            className="btn btn-secondary" style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem', textDecoration: 'none' }}>
                            🔗 Apri
                          </a>
                        )}
                        <button className="btn btn-danger" style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
                          onClick={() => handleDeleteMaterial(m.id, m.title)}>
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showAddSpeaker && (
        <AddSpeakerModal editionId={editionId!} onClose={() => setShowAddSpeaker(false)} onAdded={load} />
      )}
      {showAddMaterial && (
        <AddMaterialModal editionId={editionId!} onClose={() => setShowAddMaterial(false)} onAdded={load} />
      )}
    </div>
  );
}
